/**
 * TopPriorityTile - Prominent display of top priority tasks
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Panel, Spinner } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';

interface Task {
  id: number;
  description: string;
  due_date?: string;
  project?: { name: string };
}

interface TopPriorityTileProps {
  tasks: Task[];
  loading?: boolean;
  onMarkDone?: (taskId: number) => void;
}

const TopPriorityTile: React.FC<TopPriorityTileProps> = ({ tasks, loading = false, onMarkDone }) => {
  if (loading) {
    return (
      <Panel style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="md" />
      </Panel>
    );
  }

  if (tasks.length === 0) {
    return null; // Don't show if no top priority tasks
  }

  return (
    <Panel
      style={{
        backgroundColor: `${COLORS.status.error}08`,
        border: `2px solid ${COLORS.status.error}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: COLORS.status.error }} />
          <span
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.status.error,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Top Priority
          </span>
        </div>
        <Link to="/tasks" style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.accent.default, textDecoration: 'none' }}>
          View All
        </Link>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[8] }}>
        {tasks.map((task) => (
          <div
            key={task.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: SPACING[12],
              padding: SPACING[12],
              backgroundColor: COLORS.bg.panel,
              borderRadius: '6px',
              border: `1px solid ${COLORS.border.default}`,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  fontWeight: '600',
                  color: COLORS.text.primary,
                  marginBottom: task.project ? SPACING[4] : 0,
                }}
              >
                {task.description}
              </div>
              {task.project && (
                <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
                  {task.project.name}
                </div>
              )}
            </div>
            {onMarkDone && (
              <button
                onClick={() => onMarkDone(task.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[4],
                  padding: `${SPACING[4]} ${SPACING[12]}`,
                  borderRadius: '4px',
                  border: `1px solid ${COLORS.status.success}`,
                  backgroundColor: 'transparent',
                  color: COLORS.status.success,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <CheckIcon style={{ width: '14px', height: '14px' }} />
                Done
              </button>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
};

export default TopPriorityTile;
